<!--
  Ported from vendor-ref/mara/app/ui/src/components/nanome/SequenceRow.vue: one entry's sequence as an SVG (serial
  ticks + one-letter codes, one text span per colour run, one rect per overlay run), a chain row above it, click /
  drag range select, chain click toggles the chain, edge auto-scroll while dragging. Lite adds shift-click to extend
  from the last clicked residue.
-->
<script setup lang="ts">
import { computed, inject, onUnmounted, ref, type Ref } from 'vue';
import { useEventListener, useRafFn } from '@vueuse/core';
import { sequenceEntryKey, type SequenceEntry, type SequenceResidue } from '@nanome-lite/state';
import { useSequenceStore } from '@/sequence/store';

const props = defineProps<{ entry: SequenceEntry }>();

const seq = useSequenceStore();

// Per-residue cell width keeps the chain / serial / residue rows character-aligned. Single-char codes use CELL;
// multi-char codes (ligands like HEM) widen to fit so they don't overflow into the next cell.
const CELL = 13;
const CHAR = 8; // glyph pitch at 12px monospace
const SERIAL_H = 12;
const RESIDUE_H = 16;

// Overlay states. Under states draw behind the residue text; over states draw on top with their own white copy.
const UNDER: Record<string, { fill: string; y: number }> = {
  hover: { fill: '#c49cfc', y: 0 },
  partial: { fill: '#9caffc', y: RESIDUE_H / 2 },
};
const OVER: Record<string, string> = {
  selected: '#9600FF',
  active: '#5d2a99', // selected or partial + hovered
};

function categoryColor(r: SequenceResidue): string {
  if (r.missing) return '#888';
  switch (r.category) {
    case 'nonStandard': return '#fce99c';
    case 'water': return '#ff6b6b';
    case 'ligand': return 'var(--primary-color)';
    default: return 'var(--text-color)';
  }
}

/** Group equal adjacent values into { lo, hi, value } runs. Falsy values make no run. */
function runs<T>(values: T[]): { lo: number; hi: number; value: T }[] {
  const out: { lo: number; hi: number; value: T }[] = [];
  let lo = 0;
  for (let i = 1; i <= values.length; i++) {
    if (i < values.length && values[i] === values[lo]) continue;
    if (values[lo]) out.push({ lo, hi: i - 1, value: values[lo]! });
    lo = i;
  }
  return out;
}

const key = computed(() => sequenceEntryKey(props.entry));

// The shared horizontal/vertical scroll surface (provided by WorkspaceSequenceMenu).
const scrollEl = inject<Ref<HTMLElement | null> | null>('sequenceScroll', null);
const svgEl = ref<SVGSVGElement | null>(null);

// Static cell geometry. Rebuilds only when the entry changes.
const layout = computed(() => {
  const residues = props.entry.residues;
  const lefts = new Float64Array(residues.length);
  const rights = new Float64Array(residues.length);
  let x = 0;
  residues.forEach((r, i) => {
    lefts[i] = x;
    x += Math.max(CELL, r.code.length * CHAR);
    rights[i] = x;
  });
  return { lefts, rights, width: x };
});

/** Text and per-glyph x list for residues lo..hi; each explicit x centres its glyph. */
function glyphs(lo: number, hi: number) {
  const residues = props.entry.residues;
  const { lefts, rights } = layout.value;
  let text = '';
  const xs: number[] = [];
  for (let i = lo; i <= hi; i++) {
    const code = residues[i]!.code;
    const first = (lefts[i]! + rights[i]! - (code.length - 1) * CHAR) / 2;
    for (let j = 0; j < code.length; j++) xs.push(first + j * CHAR);
    text += code;
  }
  return { text, xs: xs.join(' ') };
}

const textRuns = computed(() =>
  runs(props.entry.residues.map(categoryColor)).map(({ lo, hi, value }) => ({ lo, color: value, ...glyphs(lo, hi) })),
);

const serialLabels = computed(() => {
  const { lefts, rights } = layout.value;
  const labels: { x: number; label: string }[] = [];
  props.entry.residues.forEach((r, i) => {
    if (r.serialLabel != null) labels.push({ x: (lefts[i]! + rights[i]!) / 2, label: r.serialLabel });
  });
  return labels;
});

// One segment per chain, spanning that chain's residues, so the whole span is hoverable/clickable.
const chainSegments = computed(() => {
  const { lefts, rights } = layout.value;
  const segments: { chain: string; left: number; width: number; firstWidth: number }[] = [];
  let current: (typeof segments)[number] | null = null;
  props.entry.residues.forEach((r, i) => {
    if (r.chainStart || !current) {
      current = { chain: r.chain, left: lefts[i]!, width: 0, firstWidth: rights[i]! - lefts[i]! };
      segments.push(current);
    }
    current.width = rights[i]! - current.left;
  });
  return segments;
});

const rowHover = computed(() => (seq.hover?.entryKey === key.value ? seq.hover : null));

const overlay = computed(() => {
  const residues = props.entry.residues;
  const n = residues.length;
  const states: (string | null)[] = Array.from({ length: n }, () => null);
  const s = seq.selection.get(key.value);
  if (s) {
    for (const i of s.partial) if (i < n) states[i] = 'partial';
    for (const i of s.selected) if (i < n) states[i] = 'selected';
  }
  const hover = (i: number) => { states[i] = states[i] ? 'active' : 'hover'; };
  const h = rowHover.value;
  if (h?.kind === 'residue' && h.index !== undefined && h.index < n) hover(h.index);
  else if (h?.kind === 'chain') residues.forEach((r, i) => { if (!r.missing && r.chain === h.chain) hover(i); });

  const { lefts, rights } = layout.value;
  const under: { lo: number; x: number; width: number; fill: string; y: number }[] = [];
  const over: { lo: number; x: number; width: number; fill: string; text: string; xs: string }[] = [];
  for (const { lo, hi, value } of runs(states)) {
    const rect = { lo, x: lefts[lo]!, width: rights[hi]! - lefts[lo]! };
    const u = UNDER[value!];
    if (u) under.push({ ...rect, ...u });
    else over.push({ ...rect, fill: OVER[value!]!, ...glyphs(lo, hi) });
  }
  return { under, over };
});

// ---- gesture state (click + click-drag range select) ----
let dragging = false;
let mode: 'select' | 'deselect' = 'select';
let anchor = -1;
let dragIndex = -1;
let baseSelected = new Set<number>();
let basePartial = new Set<number>();
let pointerX = 0;

// Auto-scroll near the edges while dragging.
const EDGE = 24;
const { pause: stopAutoScroll, resume: startAutoScroll } = useRafFn(() => {
  const el = scrollEl?.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  if (pointerX < rect.left + EDGE) el.scrollLeft -= 8;
  else if (pointerX > rect.right - EDGE) el.scrollLeft += 8;
  else return;
  updateDrag();
}, { immediate: false });

/** Client X -> residue index (binary search over cell right edges). */
function residueIndexAtX(clientX: number): number {
  const el = svgEl.value;
  const { rights } = layout.value;
  if (!el || !rights.length) return -1;
  const x = Math.max(0, clientX - el.getBoundingClientRect().left);
  let lo = 0;
  let hi = rights.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (rights[mid]! > x) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

function onPointerDown(event: PointerEvent) {
  const residue = props.entry.residues[residueIndexAtX(event.clientX)];
  if (!residue || residue.missing) return; // missing residues are inert
  const s = seq.slice(key.value);
  const extend = event.shiftKey && seq.anchor?.entryKey === key.value;
  // An already-selected anchor means the drag deselects; shift always extends the selection.
  mode = !extend && (s.selected.has(residue.index) || s.partial.has(residue.index)) ? 'deselect' : 'select';
  anchor = extend ? seq.anchor!.index : residue.index;
  if (!extend) seq.anchor = { entryKey: key.value, index: residue.index };
  baseSelected = new Set(s.selected);
  basePartial = new Set(s.partial);
  pointerX = event.clientX;
  dragging = true;
  dragIndex = residue.index;
  seq.setSuppressEcho(true); // local selection wins until the gesture ends
  seq.beginEdit();
  svgEl.value?.setPointerCapture?.(event.pointerId);
  applyRange(residue.index);
  startAutoScroll();
}

function onPointerMove(event: PointerEvent) {
  if (dragging) return;
  const residue = props.entry.residues[residueIndexAtX(event.clientX)];
  if (residue) seq.setHoverResidue(props.entry, residue);
}

function updateDrag() {
  const idx = residueIndexAtX(pointerX);
  if (idx < 0 || idx === dragIndex) return;
  dragIndex = idx;
  applyRange(idx);
  seq.setHoverResidue(props.entry, props.entry.residues[idx]!);
}

function applyRange(currentIndex: number) {
  const lo = Math.min(anchor, currentIndex);
  const hi = Math.max(anchor, currentIndex);
  const selected = new Set(baseSelected);
  const partial = new Set(basePartial);
  for (let i = lo; i <= hi; i++) {
    const r = props.entry.residues[i];
    if (!r || r.missing) continue;
    if (mode === 'select') { selected.add(i); partial.delete(i); }
    else { selected.delete(i); partial.delete(i); }
  }
  seq.applyGesture(key.value, selected, partial);
  seq.commit();
}

function endGesture() {
  if (!dragging) return;
  dragging = false;
  stopAutoScroll();
  seq.flush();
  seq.setSuppressEcho(false);
}

function onChainClick(chain: string) {
  const residues = props.entry.residues.filter(r => !r.missing && r.chain === chain);
  const s = seq.slice(key.value);
  const anySelected = residues.some(r => s.selected.has(r.index) || s.partial.has(r.index));
  const indices = residues.map(r => r.index);
  seq.beginEdit();
  if (anySelected) seq.deselectResidues(key.value, indices);
  else seq.selectResidues(key.value, indices);
  seq.flush();
}

useEventListener(window, 'pointermove', (event: PointerEvent) => {
  if (!dragging) return;
  if (!event.buttons) { endGesture(); return; }
  pointerX = event.clientX;
  updateDrag();
});
useEventListener(window, ['pointerup', 'pointercancel'], endGesture);

onUnmounted(() => { if (dragging) seq.setSuppressEcho(false); });
</script>

<template>
  <div class="flex w-full b-b b-section" @pointerleave="seq.clearHover()">
    <!-- Name column, pinned to the left during the shared horizontal scroll -->
    <div class="sticky left-0 z-10 w-32 shrink-0 flex items-center px-2 b-r b-section bg-ground" :title="entry.entryName">
      <span class="min-w-0 truncate text-sm">{{ entry.entryName }}</span>
    </div>

    <!-- Sequence block (scrolls with the shared horizontal scrollbar) -->
    <div class="shrink-0 py-1 select-none touch-none" data-testid="sequence-block">
      <!-- Chain row: one hoverable/clickable segment per chain -->
      <div class="flex h-3.5">
        <div
          v-for="(seg, i) in chainSegments"
          :key="`c${i}`"
          class="flex shrink-0 items-end font-mono text-[11px] font-bold leading-none cursor-pointer"
          :style="{ width: `${seg.width}px` }"
          data-testid="sequence-chain"
          @pointerenter="seq.setHoverChain(entry, seg.chain)"
          @click="onChainClick(seg.chain)"
        >
          <span class="sticky left-32 bg-ground text-center" :style="{ minWidth: `${seg.firstWidth}px` }">{{ seg.chain || '_' }}</span>
        </div>
      </div>

      <svg
        ref="svgEl"
        class="block font-mono cursor-pointer"
        :width="layout.width"
        :height="SERIAL_H + RESIDUE_H"
        @pointerdown.prevent="onPointerDown"
        @pointermove="onPointerMove"
      >
        <text class="text-[9px] op-50" fill="currentColor" text-anchor="middle" :y="SERIAL_H - 1">
          <tspan v-for="s in serialLabels" :key="s.x" :x="s.x">{{ s.label }}</tspan>
        </text>

        <g class="text-[12px]" :transform="`translate(0 ${SERIAL_H})`" text-anchor="middle" dominant-baseline="central">
          <rect v-for="o in overlay.under" :key="`u${o.lo}`" :x="o.x" :y="o.y" :width="o.width" :height="RESIDUE_H - o.y" :fill="o.fill" />
          <text :y="RESIDUE_H / 2">
            <tspan v-for="t in textRuns" :key="t.lo" :x="t.xs" :style="{ fill: t.color }">{{ t.text }}</tspan>
          </text>
          <g v-for="o in overlay.over" :key="`o${o.lo}`">
            <rect :x="o.x" :width="o.width" :height="RESIDUE_H" :fill="o.fill" />
            <text :x="o.xs" :y="RESIDUE_H / 2" fill="#fff">{{ o.text }}</text>
          </g>
        </g>
      </svg>
    </div>
  </div>
</template>
